import React, { useState, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Upload, FileText, AlertCircle, CheckCircle, Download, Info, Shield } from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import Papa from 'papaparse'
import * as XLSX from 'xlsx'

interface MailboxUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploadComplete: () => void
}

interface MailboxData {
  // Required fields
  email: string
  domain: string
  from_name: string
  provider: string
  // Optional fields - only included if provided in CSV
  subdomain?: string
  daily_limit?: number
  notes?: string
  status?: string
  current_daily_sent?: number
  smartlead_id?: number
  // NEW: User-specific fields from CSV
  password?: string  // SMTP/IMAP password
  client_name?: string  // Will be converted to client_id
  profile_picture?: string
  signature?: string
  min_time_gap?: number  // Minutes between emails
  is_workspace_admin?: boolean  // Google Workspace admin flag
  // Note: health_score, bounce_rate, replies_7d, emails_sent_7d, bounces_7d, total_bounces
  // are not fields in the mailboxes table - they belong to domains table or are calculated fields
}

interface DuplicateCheckResult {
  newMailboxes: MailboxData[]
  databaseDuplicates: Array<{ csvEmail: string; dbEmail: string }>
  csvDuplicates: Array<{ email: string; occurrences: number }>
}

export function MailboxUploadDialog({ open, onOpenChange, onUploadComplete }: MailboxUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<MailboxData[]>([])
  const [duplicateCheck, setDuplicateCheck] = useState<DuplicateCheckResult | null>(null)
  const [uploading, setUploading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [uploadResults, setUploadResults] = useState<{
    successCount: number
    skippedCount: number
    failedCount: number
    adminConfigured: number
    errors: string[]
  } | null>(null)
  const { toast } = useToast()

  // Normalize email for comparison
  const normalizeEmail = (email: string): string => {
    return email.toLowerCase().trim()
  }

  const downloadTemplate = () => {
    const templateData = [
      {
        'Mailboxes': 'sebastian@getcruva.com',
        'User Full Name': 'Sebastian Nelson',
        'Password of your inboxes': 'your_password_here',
        'Profile Picture of your inboxes': 'https://example.com/photos/sebastian.jpg',
        'Signature': '<p>Best regards,<br>Sebastian Nelson<br>CEO, GetCruva</p>',
        'Client': 'GetCruva',
        'Message Per Day (Warmups not included)': '50',
        'Minimum time gap (min)': '5',
        'Google Workspace Admin': 'true',
        'Provider': 'Gmail',
        'Status': 'Active',
        'Notes': 'Workspace admin account'
      },
      {
        'Mailboxes': 'sebastian.nelson@getcruva.com',
        'User Full Name': 'Sebastian Nelson',
        'Password of your inboxes': 'password123',
        'Profile Picture of your inboxes': 'https://example.com/photos/sebastian2.jpg',
        'Signature': '<p>Cheers,<br>Sebastian</p>',
        'Client': 'GetCruva',
        'Message Per Day (Warmups not included)': '50',
        'Minimum time gap (min)': '3',
        'Google Workspace Admin': 'false',
        'Provider': 'Gmail',
        'Status': 'Active',
        'Notes': 'Regular mailbox'
      },
      {
        'Mailboxes': 'max@cruvawork.com',
        'User Full Name': 'Max Cho',
        'Password of your inboxes': 'secure_password',
        'Profile Picture of your inboxes': 'https://example.com/photos/max.jpg',
        'Signature': '<p>Thanks,<br>Max Cho<br>CTO, CruvaWork</p>',
        'Client': 'CruvaWork',
        'Message Per Day (Warmups not included)': '100',
        'Minimum time gap (min)': '5',
        'Google Workspace Admin': 'true',
        'Provider': 'Gmail',
        'Status': 'Active',
        'Notes': 'Workspace admin for cruvawork.com'
      }
    ]
    
    const csv = Papa.unparse(templateData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `mailbox-template-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    toast({
      title: "Template Downloaded",
      description: "Fill out the template with your mailbox data and upload it back."
    })
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const uploadedFile = acceptedFiles[0]
    if (!uploadedFile) return

    setFile(uploadedFile)
    setUploadResults(null)
    setDuplicateCheck(null)
    parseFile(uploadedFile)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  })

  const parseFile = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase()

    if (fileExtension === 'csv') {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const parsed = parseMailboxData(results.data)
          setPreview(parsed.data)
          setErrors(parsed.errors)
          if (parsed.data.length > 0 && parsed.errors.length === 0) {
            checkForDuplicates(parsed.data)
          }
        },
        error: (error) => {
          setErrors([`CSV parsing error: ${error.message}`])
        }
      })
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          const workbook = XLSX.read(data, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet)
          
          const parsed = parseMailboxData(jsonData)
          setPreview(parsed.data)
          setErrors(parsed.errors)
          if (parsed.data.length > 0 && parsed.errors.length === 0) {
            checkForDuplicates(parsed.data)
          }
        } catch (error) {
          setErrors([`Excel parsing error: ${error instanceof Error ? error.message : 'Unknown error'}`])
        }
      }
      reader.readAsArrayBuffer(file)
    }
  }

  const findIntraCSVDuplicates = (mailboxes: MailboxData[]): { 
    uniqueMailboxes: MailboxData[], 
    csvDuplicates: Array<{ email: string; occurrences: number }> 
  } => {
    const emailCounts = new Map<string, number>()
    const seenEmails = new Set<string>()
    const uniqueMailboxes: MailboxData[] = []
    
    // Count occurrences and keep first occurrence
    mailboxes.forEach(mailbox => {
      const normalizedEmail = normalizeEmail(mailbox.email)
      const currentCount = emailCounts.get(normalizedEmail) || 0
      emailCounts.set(normalizedEmail, currentCount + 1)
      
      // Keep only the first occurrence
      if (!seenEmails.has(normalizedEmail)) {
        seenEmails.add(normalizedEmail)
        uniqueMailboxes.push({
          ...mailbox,
          email: normalizedEmail // Store normalized version
        })
      }
    })
    
    // Find duplicates (emails that appear more than once)
    const csvDuplicates = Array.from(emailCounts.entries())
      .filter(([_, count]) => count > 1)
      .map(([email, occurrences]) => ({ email, occurrences }))
    
    console.log(`📊 CSV duplicate analysis: ${uniqueMailboxes.length} unique, ${csvDuplicates.length} duplicated emails`)
    csvDuplicates.forEach(({ email, occurrences }) => {
      console.log(`🔄 CSV duplicate: ${email} appears ${occurrences} times`)
    })
    
    return { uniqueMailboxes, csvDuplicates }
  }

  const checkForDuplicates = async (mailboxes: MailboxData[]) => {
    try {
      console.log('🔍 Starting comprehensive duplicate check...')
      
      // Step 1: Find and remove intra-CSV duplicates
      const { uniqueMailboxes, csvDuplicates } = findIntraCSVDuplicates(mailboxes)
      
      // Step 2: Check unique emails against database
      const normalizedEmails = uniqueMailboxes.map(m => normalizeEmail(m.email))
      
      console.log(`🔍 Checking ${normalizedEmails.length} unique emails against database...`)
      console.log('📧 Emails to check:', normalizedEmails.slice(0, 5).join(', '), normalizedEmails.length > 5 ? '...' : '')
      
      const { data: existingMailboxes, error } = await supabase
        .from('mailboxes')
        .select('email')
        .or(normalizedEmails.map(email => `email.ilike.${email}`).join(','))

      if (error) {
        console.error('❌ Error checking for duplicates:', error)
        return
      }

      console.log(`📊 Database query returned ${existingMailboxes?.length || 0} existing emails`)
      existingMailboxes?.forEach(existing => {
        console.log(`🗃️ Existing in DB: ${existing.email}`)
      })

      // Step 3: Create mapping of database duplicates
      const existingEmailsSet = new Set(
        (existingMailboxes || []).map(m => normalizeEmail(m.email))
      )
      
      const newMailboxes: MailboxData[] = []
      const databaseDuplicates: Array<{ csvEmail: string; dbEmail: string }> = []
      
      uniqueMailboxes.forEach(mailbox => {
        const normalizedEmail = normalizeEmail(mailbox.email)
        if (existingEmailsSet.has(normalizedEmail)) {
          // Find the exact match from database
          const dbMatch = existingMailboxes?.find(existing => 
            normalizeEmail(existing.email) === normalizedEmail
          )
          databaseDuplicates.push({
            csvEmail: mailbox.email,
            dbEmail: dbMatch?.email || normalizedEmail
          })
          console.log(`⚠️ Database duplicate: CSV ${mailbox.email} matches DB ${dbMatch?.email}`)
        } else {
          newMailboxes.push(mailbox)
        }
      })

      console.log(`📊 Final results: ${newMailboxes.length} new, ${databaseDuplicates.length} DB duplicates, ${csvDuplicates.length} CSV duplicates`)
      
      setDuplicateCheck({
        newMailboxes,
        databaseDuplicates,
        csvDuplicates
      })
    } catch (error) {
      console.error('❌ Error in duplicate check:', error)
    }
  }

  const findColumnValue = (row: any, possibleNames: string[]): string | undefined => {
    for (const name of possibleNames) {
      if (row[name] !== undefined && row[name] !== null && row[name] !== '') {
        return String(row[name]).trim()
      }
    }
    return undefined
  }

  const isEmptyRow = (row: any): boolean => {
    if (!row || typeof row !== 'object') {
      return true
    }

    const values = Object.values(row)
    
    const isEmpty = values.every(value => {
      if (value === null || value === undefined) return true
      if (typeof value === 'string') return value.trim() === ''
      return false
    })

    return isEmpty
  }

  const parseMailboxData = (rawData: any[]): { data: MailboxData[], errors: string[] } => {
    const data: MailboxData[] = []
    const errors: string[] = []
    let emptyRowsSkipped = 0

    console.log('🔍 Starting to parse mailbox data from raw data:', rawData.length, 'total rows')

    rawData.forEach((row, index) => {
      try {
        if (isEmptyRow(row)) {
          emptyRowsSkipped++
          console.log(`⏭️ Skipping empty row at index ${index + 1}`)
          return
        }

        const email = findColumnValue(row, [
          'email', 'Email', 'EMAIL', 'EmailAddress', 'Email Address', 'email_address',
          'Mailboxes', 'mailboxes', 'MAILBOXES'
        ])
        
        const fromName = findColumnValue(row, [
          'from_name', 'From Name', 'fromName', 'DisplayName', 'Display Name', 
          'display_name', 'name', 'Name', 'NAME', 'User Full Name', 'user_full_name'
        ])
        
        const provider = findColumnValue(row, [
          'provider', 'Provider', 'PROVIDER', 'Mailbox Provider', 'MailboxProvider', 
          'mailbox_provider', 'Email Provider', 'EmailProvider', 'email_provider'
        ])
        
        if (!email) {
          errors.push(`Row ${index + 1}: Email is required`)
          return
        }
        
        if (!fromName) {
          errors.push(`Row ${index + 1}: From Name/Display Name is required`)
          return
        }
        
        if (!provider) {
          errors.push(`Row ${index + 1}: Provider is required`)
          return
        }

        // Normalize email during parsing
        const normalizedEmail = normalizeEmail(email)
        const domain = normalizedEmail.split('@')[1]
        if (!domain) {
          errors.push(`Row ${index + 1}: Invalid email format`)
          return
        }

        const subdomain = findColumnValue(row, [
          'subdomain', 'Subdomain', 'SUBDOMAIN', 'sub_domain'
        ])
        
        const dailyLimitStr = findColumnValue(row, [
          'daily_limit', 'Daily Limit', 'DailyLimit', 'daily_limit', 'limit',
          'Message Per Day (Warmups not included)', 'message_per_day', 'messages_per_day'
        ])
        
        const notes = findColumnValue(row, [
          'notes', 'Notes', 'NOTES', 'note', 'Note', 'comments', 'Comments'
        ])

        const status = findColumnValue(row, [
          'status', 'Status', 'STATUS', 'mailbox_status'
        ])

        const healthScoreStr = findColumnValue(row, [
          'health_score', 'Health Score', 'HealthScore', 'health', 'Health'
        ])

        // Note: bounce_rate is not a field in the mailboxes table, it's in domains table
        // const bounceRateStr = findColumnValue(row, [
        //   'bounce_rate', 'Bounce Rate', 'BounceRate', 'bounce', 'Bounce'
        // ])

        const currentDailySentStr = findColumnValue(row, [
          'current_daily_sent', 'Current Daily Sent', 'CurrentDailySent', 'daily_sent', 'sent_today'
        ])

        const smartleadIdStr = findColumnValue(row, [
          'smartlead_id', 'Smartlead ID', 'SmartleadID', 'id', 'ID'
        ])

        const replies7dStr = findColumnValue(row, [
          'replies_7d', 'Replies 7d', 'Replies7d', 'replies', 'Replies'
        ])

        const emailsSent7dStr = findColumnValue(row, [
          'emails_sent_7d', 'Emails Sent 7d', 'EmailsSent7d', 'sent_7d', 'emails_sent'
        ])

        const bounces7dStr = findColumnValue(row, [
          'bounces_7d', 'Bounces 7d', 'Bounces7d', 'bounces_week'
        ])

        const totalBouncesStr = findColumnValue(row, [
          'total_bounces', 'Total Bounces', 'TotalBounces', 'total_bounce'
        ])

        // NEW: Additional fields for enhanced mailbox management
        const password = findColumnValue(row, [
          'password', 'Password', 'PASSWORD',
          'Password of your inboxes', 'password_of_your_inboxes',
          'smtp_password', 'SMTP Password', 'imap_password'
        ])
        
        const profilePicture = findColumnValue(row, [
          'profile_picture', 'Profile Picture', 'ProfilePicture',
          'Profile Picture of your inboxes', 'profile_picture_of_your_inboxes',
          'photo', 'Photo', 'avatar', 'Avatar', 'image'
        ])
        
        const signature = findColumnValue(row, [
          'signature', 'Signature', 'SIGNATURE',
          'email_signature', 'Email Signature', 'EmailSignature'
        ])
        
        const clientName = findColumnValue(row, [
          'client', 'Client', 'CLIENT',
          'client_name', 'Client Name', 'ClientName'
        ])
        
        const minTimeGapStr = findColumnValue(row, [
          'minimum_time_gap', 'Minimum time gap (min)', 'minimum_time_gap_min',
          'min_time_gap', 'time_gap', 'gap_minutes', 'Minimum time gap'
        ])
        
        const isWorkspaceAdminStr = findColumnValue(row, [
          'google_workspace_admin', 'Google Workspace Admin', 'google_admin',
          'is_workspace_admin', 'workspace_admin', 'admin',
          'is_admin', 'Admin', 'ADMIN'
        ])

        // Build the mailbox data object with only provided fields
        const mailboxData: any = {
          email: normalizedEmail, // Store normalized email
          domain,
          from_name: fromName,
          provider
        }

        // Only add optional fields if they have values
        if (subdomain && subdomain.trim()) {
          mailboxData.subdomain = subdomain.trim()
        }
        
        if (dailyLimitStr && dailyLimitStr.trim()) {
          const parsedLimit = parseInt(dailyLimitStr)
          if (!isNaN(parsedLimit) && parsedLimit > 0) {
            mailboxData.daily_limit = parsedLimit
          }
        }
        
        if (notes && notes.trim()) {
          mailboxData.notes = notes.trim()
        }
        
        if (status && status.trim()) {
          // Normalize status to valid database values
          const normalizedStatus = status.trim().toUpperCase()
          if (normalizedStatus === 'ACTIVE' || normalizedStatus === 'INACTIVE' || normalizedStatus === 'DEACTIVATED' || normalizedStatus === 'NULL' || 
              normalizedStatus === 'ORDERED/PURCHASED' || normalizedStatus === 'WAITING FOR UPLOAD') {
            mailboxData.status = normalizedStatus
          } else {
            // Handle common variations
            if (normalizedStatus === 'DISABLED' || normalizedStatus === 'OFF') {
              mailboxData.status = 'DEACTIVATED'
            } else if (normalizedStatus === 'ENABLED' || normalizedStatus === 'ON' || normalizedStatus === 'RUNNING') {
              mailboxData.status = 'ACTIVE'
            } else if (normalizedStatus === 'ORDERED' || normalizedStatus === 'PURCHASED') {
              mailboxData.status = 'ORDERED/PURCHASED'
            } else if (normalizedStatus === 'WAITING' || normalizedStatus === 'UPLOAD' || normalizedStatus === 'PENDING UPLOAD') {
              mailboxData.status = 'WAITING FOR UPLOAD'
            } else {
              // Default to ORDERED/PURCHASED for unknown status values
              mailboxData.status = 'ORDERED/PURCHASED'
            }
          }
        } else {
          // If no status is provided, default to ORDERED/PURCHASED
          mailboxData.status = 'ORDERED/PURCHASED'
        }
        
        if (currentDailySentStr && currentDailySentStr.trim()) {
          const parsedSent = parseInt(currentDailySentStr)
          if (!isNaN(parsedSent) && parsedSent >= 0) {
            mailboxData.current_daily_sent = parsedSent
          }
        }
        
        if (smartleadIdStr && smartleadIdStr.trim()) {
          const parsedId = parseInt(smartleadIdStr)
          if (!isNaN(parsedId) && parsedId > 0) {
            mailboxData.smartlead_id = parsedId
          }
        }

        // NEW: Add enhanced mailbox fields
        if (password && password.trim()) {
          mailboxData.password = password.trim()
        }
        
        if (profilePicture && profilePicture.trim()) {
          mailboxData.profile_picture = profilePicture.trim()
        }
        
        if (signature && signature.trim()) {
          mailboxData.signature = signature.trim()
        }
        
        if (clientName && clientName.trim()) {
          mailboxData.client_name = clientName.trim()
        }
        
        if (minTimeGapStr && minTimeGapStr.trim()) {
          const parsedGap = parseInt(minTimeGapStr)
          if (!isNaN(parsedGap) && parsedGap > 0) {
            mailboxData.min_time_gap = parsedGap
          }
        }
        
        if (isWorkspaceAdminStr && isWorkspaceAdminStr.trim()) {
          const normalizedAdmin = isWorkspaceAdminStr.trim().toLowerCase()
          mailboxData.is_workspace_admin = (
            normalizedAdmin === 'true' || 
            normalizedAdmin === 'yes' || 
            normalizedAdmin === '1' ||
            normalizedAdmin === 'y'
          )
        } else {
          mailboxData.is_workspace_admin = false
        }

        data.push(mailboxData)
      } catch (error) {
        errors.push(`Row ${index + 1}: ${error instanceof Error ? error.message : 'Parse error'}`)
      }
    })

    console.log(`📊 Parsing complete: ${data.length} valid mailboxes, ${emptyRowsSkipped} empty rows skipped, ${errors.length} errors`)
    
    return { data, errors }
  }

  const handleUpload = async () => {
    if (!duplicateCheck || duplicateCheck.newMailboxes.length === 0) return

    setUploading(true)
    setUploadResults(null)
    
    try {
      console.log('Starting mailbox upload...', duplicateCheck.newMailboxes.length, 'new mailboxes')
      
      // Step 1: Get all clients for name lookup
      const { data: clients } = await supabase
        .from('clients')
        .select('id, name')
      
      const clientMap = new Map(
        clients?.map(c => [c.name.toLowerCase().trim(), c.id]) || []
      )
      
      let successCount = 0
      let failedCount = 0
      let adminConfigured = 0
      const uploadErrors: string[] = []

      // Process each mailbox individually to handle admin logic
      for (const mailbox of duplicateCheck.newMailboxes) {
        try {
          // Look up client_id from client_name
          let client_id = null
          if (mailbox.client_name) {
            client_id = clientMap.get(mailbox.client_name.toLowerCase().trim())
            if (!client_id) {
              console.warn(`⚠️ Client "${mailbox.client_name}" not found in database`)
            }
          }
          
          // Prepare mailbox data for insert
          const mailboxInsert: any = {
            email: mailbox.email,
            from_name: mailbox.from_name,
            smtp_password: mailbox.password,
            imap_password: mailbox.password,
            profile_picture_url: mailbox.profile_picture,
            signature: mailbox.signature,
            min_time_gap_minutes: mailbox.min_time_gap,
            is_workspace_admin: mailbox.is_workspace_admin || false,
            daily_limit: mailbox.daily_limit,
            status: mailbox.status,
            notes: mailbox.notes,
            client_id: client_id
          }
          
          // Insert/update mailbox
          const { data: insertedMailbox, error: mailboxError } = await supabase
            .from('mailboxes')
            .upsert(mailboxInsert, {
              onConflict: 'email'
            })
            .select()
            .single()
          
          if (mailboxError) {
            console.error(`Error inserting mailbox ${mailbox.email}:`, mailboxError)
            uploadErrors.push(`${mailbox.email}: ${mailboxError.message}`)
            failedCount++
            continue
          }
          
          successCount++
          console.log(`✅ Mailbox ${mailbox.email} uploaded successfully`)
          
          // Step 2: If workspace admin, update domain table
          if (mailbox.is_workspace_admin && insertedMailbox) {
            const domain = mailbox.email.split('@')[1]
            
            const { error: domainError } = await supabase
              .from('domains')
              .update({
                workspace_admin_email: mailbox.email,
                workspace_admin_mailbox_id: insertedMailbox.id,
                updated_at: new Date().toISOString()
              })
              .eq('domain_name', domain)
            
            if (!domainError) {
              adminConfigured++
              console.log(`🛡️ Set ${mailbox.email} as workspace admin for ${domain}`)
            } else {
              console.warn(`⚠️ Failed to update domain admin for ${domain}:`, domainError)
            }
          }
          
        } catch (error) {
          console.error(`Error processing mailbox ${mailbox.email}:`, error)
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          uploadErrors.push(`${mailbox.email}: ${errorMessage}`)
          failedCount++
        }
      }

      const totalSkipped = duplicateCheck.databaseDuplicates.length + duplicateCheck.csvDuplicates.length

      setUploadResults({
        successCount,
        skippedCount: totalSkipped,
        failedCount,
        adminConfigured,
        errors: uploadErrors
      })

      if (successCount > 0 && failedCount === 0) {
        const adminMsg = adminConfigured > 0 ? `\n🛡️ ${adminConfigured} workspace admin${adminConfigured > 1 ? 's' : ''} configured` : ''
        toast({
          title: "Upload Successful",
          description: `Successfully uploaded ${successCount} new mailboxes${totalSkipped > 0 ? `, ${totalSkipped} duplicates skipped` : ''}${adminMsg}`
        })
        onUploadComplete()
        resetDialog()
      } else if (successCount > 0 && failedCount > 0) {
        const adminMsg = adminConfigured > 0 ? `, ${adminConfigured} admins configured` : ''
        toast({
          title: "Partial Upload Success",
          description: `${successCount} mailboxes uploaded, ${failedCount} failed, ${totalSkipped} duplicates skipped${adminMsg}`
        })
      } else {
        toast({
          title: "Upload Failed",
          description: `All ${failedCount} mailboxes failed to upload. Check error details below.`,
          variant: "destructive"
        })
      }

    } catch (error) {
      console.error('Upload failed:', error)
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred"
      const totalSkipped = duplicateCheck.databaseDuplicates.length + duplicateCheck.csvDuplicates.length
      setUploadResults({
        successCount: 0,
        skippedCount: totalSkipped,
        failedCount: duplicateCheck.newMailboxes.length,
        adminConfigured: 0,
        errors: [errorMessage]
      })
      toast({
        title: "Upload Failed",
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setUploading(false)
    }
  }

  const resetDialog = () => {
    setFile(null)
    setPreview([])
    setErrors([])
    setUploadResults(null)
    setDuplicateCheck(null)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Mailboxes</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Template Download Section */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex-1">
              <Label className="text-base font-medium text-blue-900">Need a template?</Label>
              <p className="text-sm text-blue-700 mt-1">
                Download our CSV template with sample data to ensure proper formatting. Fill it out with your mailbox information and upload it back.
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={downloadTemplate}
              className="border-blue-300 text-blue-700 hover:bg-blue-100 whitespace-nowrap"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </div>

          {/* File Upload Area */}
          <div>
            <Label>Upload CSV or Excel File</Label>
            <div
              {...getRootProps()}
              className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              {file ? (
                <p className="text-sm font-medium">{file.name}</p>
              ) : (
                <div>
                  <p className="text-sm font-medium">Drop your file here, or click to browse</p>
                  <p className="text-xs text-gray-500 mt-1">Supports CSV, XLS, and XLSX files</p>
                </div>
              )}
            </div>
          </div>

          {/* Required Fields Info */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Required columns (flexible names):</strong><br />
              • Email: email, EmailAddress, Email Address<br />
              • From Name: from_name, DisplayName, Display Name, Name<br />
              • Provider: provider, Mailbox Provider, Email Provider<br />
        <strong>Optional columns:</strong> subdomain, daily_limit, notes, status, current_daily_sent, smartlead_id<br />
        <strong>Status values:</strong> ACTIVE, INACTIVE, DEACTIVATED, NULL, ORDERED/PURCHASED, WAITING FOR UPLOAD (or Active, Inactive, Disabled, Enabled, Ordered, Purchased, Waiting, Upload, etc. - will be normalized)<br />
        <strong>Note:</strong> Optional columns are truly optional - if not provided, they will be omitted from the upload. Only required fields (Email, From Name, Provider) are mandatory.<br />
              <strong>Note:</strong> Analytics fields (health_score, bounce_rate, replies_7d, emails_sent_7d, bounces_7d, total_bounces) are not supported for mailbox uploads as they belong to domains table or are calculated fields.<br />
              <strong>Note:</strong> Email addresses are automatically normalized (lowercase, trimmed) for comparison.
            </AlertDescription>
          </Alert>

          {/* Enhanced Duplicate Check Results */}
          {duplicateCheck && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-3">
                  <div className="font-medium">Upload Summary:</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-700">{duplicateCheck.newMailboxes.length} new mailboxes will be added</span>
                    </div>
                    {duplicateCheck.databaseDuplicates.length > 0 && (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-orange-500" />
                        <span className="text-orange-700">{duplicateCheck.databaseDuplicates.length} existing in database</span>
                      </div>
                    )}
                    {duplicateCheck.csvDuplicates.length > 0 && (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-500" />
                        <span className="text-blue-700">{duplicateCheck.csvDuplicates.length} duplicates within CSV</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Database Duplicates Details */}
                  {duplicateCheck.databaseDuplicates.length > 0 && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                        View database duplicates ({duplicateCheck.databaseDuplicates.length})
                      </summary>
                      <div className="mt-2 p-3 bg-orange-50 rounded border text-xs max-h-32 overflow-y-auto">
                        {duplicateCheck.databaseDuplicates.map(({ csvEmail, dbEmail }, index) => (
                          <div key={index} className="text-orange-800">
                            CSV: {csvEmail} → DB: {dbEmail}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  
                  {/* CSV Duplicates Details */}
                  {duplicateCheck.csvDuplicates.length > 0 && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                        View CSV duplicates ({duplicateCheck.csvDuplicates.length})
                      </summary>
                      <div className="mt-2 p-3 bg-blue-50 rounded border text-xs max-h-32 overflow-y-auto">
                        {duplicateCheck.csvDuplicates.map(({ email, occurrences }, index) => (
                          <div key={index} className="text-blue-800">
                            {email} (appears {occurrences} times, keeping first occurrence)
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Upload Results */}
          {uploadResults && (
            <Alert variant={uploadResults.failedCount > 0 ? "destructive" : "default"}>
              {uploadResults.failedCount > 0 ? <AlertCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
              <AlertDescription>
                <div className="space-y-2">
                  <div className="font-medium">
                    Upload Complete: {uploadResults.successCount} added, {uploadResults.skippedCount} skipped, {uploadResults.failedCount} failed
                    {uploadResults.adminConfigured > 0 && (
                      <span className="ml-2 text-green-600">
                        🛡️ {uploadResults.adminConfigured} workspace admin{uploadResults.adminConfigured > 1 ? 's' : ''} configured
                      </span>
                    )}
                  </div>
                  {uploadResults.errors.length > 0 && (
                    <div>
                      <div className="font-medium mb-1">Error Details:</div>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        {uploadResults.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Parsing Errors */}
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium mb-2">Please fix the following errors:</div>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {errors.slice(0, 10).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                  {errors.length > 10 && (
                    <li>... and {errors.length - 10} more errors</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Preview */}
          {duplicateCheck && duplicateCheck.newMailboxes.length > 0 && errors.length === 0 && (
            <div>
              <Label>Preview - New Mailboxes ({duplicateCheck.newMailboxes.length})</Label>
              <div className="mt-2 border rounded-lg max-h-60 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left min-w-[180px]">Email</th>
                      <th className="px-3 py-2 text-left min-w-[120px]">From Name</th>
                      <th className="px-3 py-2 text-left min-w-[100px]">Client</th>
                      <th className="px-3 py-2 text-left min-w-[100px]">Role</th>
                      <th className="px-3 py-2 text-left min-w-[80px]">Daily Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicateCheck.newMailboxes.slice(0, 50).map((mailbox, index) => (
                      <tr key={index} className="border-b">
                        <td className="px-3 py-2">
                          <div className="truncate max-w-[160px]" title={mailbox.email}>
                            {mailbox.email}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="truncate max-w-[100px]" title={mailbox.from_name}>
                            {mailbox.from_name}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="truncate max-w-[90px]" title={mailbox.client_name || 'N/A'}>
                            {mailbox.client_name || '-'}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {mailbox.is_workspace_admin ? (
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                              <Shield className="h-3 w-3" />
                              Admin
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">Regular</span>
                          )}
                        </td>
                        <td className="px-3 py-2">{mailbox.daily_limit || 50}</td>
                      </tr>
                    ))}
                    {duplicateCheck.newMailboxes.length > 50 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-2 text-center text-gray-500">
                          ... and {duplicateCheck.newMailboxes.length - 50} more rows
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload}
              disabled={!duplicateCheck || duplicateCheck.newMailboxes.length === 0 || errors.length > 0 || uploading}
            >
              {uploading ? 'Uploading...' : `Upload ${duplicateCheck?.newMailboxes.length || 0} New Mailboxes`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
// Trigger deployment for status fix
// Force deployment trigger - Thu Oct  9 14:53:17 UTC 2025
